// Public, presentation-only Pantheon network configuration.
//
// This module is safe to ship in an unauthenticated bundle. It contains only
// the fixed identities, colors, roles, capabilities, presentation links, and
// decorative pulse intensity needed to draw the public network. It must never
// import operational services or accept authenticated Pantheon payloads.

export const PANTHEON_AGENTS = [
  {
    id: "athena",
    name: "Athena",
    color: "#8B5CF6",
    role: "Strategy Intelligence",
    capabilities: ["Planning", "Decision Architecture", "Strategic Recommendations"],
  },
  {
    id: "apollo",
    name: "Apollo",
    color: "#3B82F6",
    role: "Forecasting Intelligence",
    capabilities: ["Prediction", "Signal Analysis", "Outcome Forecasting"],
  },
  {
    id: "artemis",
    name: "Artemis",
    color: "#22C55E",
    role: "Audience Intelligence",
    capabilities: ["Audience Discovery", "Segmentation", "Targeting Insights"],
  },
  {
    id: "nike",
    name: "Nike",
    color: "#F97316",
    role: "Optimization Intelligence",
    capabilities: ["Performance Analysis", "Budget Intelligence", "Efficiency Detection"],
  },
  {
    id: "zeus",
    name: "Zeus",
    color: "#EF4444",
    role: "Governance Authority",
    capabilities: ["Policy Evaluation", "Risk Analysis", "Trust Validation"],
  },
  {
    id: "hermes",
    name: "Hermes",
    color: "#F5C542",
    role: "Execution Gateway",
    capabilities: ["Deployment Coordination", "Workflow Orchestration"],
  },
  {
    id: "hephaestus",
    name: "Hephaestus",
    color: "#EC4899",
    role: "Build Intelligence",
    capabilities: ["Campaign Construction", "Plan Preparation", "Intent Translation"],
  },
  {
    id: "poseidon",
    name: "Poseidon",
    color: "#06B6D4",
    role: "Cross Platform Orchestration",
    capabilities: ["Unified Channel Intelligence", "Platform Connectivity"],
  },
  {
    id: "themis",
    name: "Themis",
    color: "#14B8A6",
    role: "Verification Layer",
    capabilities: ["Validation", "Compliance", "Safety"],
  },
  {
    id: "hades",
    name: "Hades",
    color: "#6366F1",
    role: "Memory Vault",
    capabilities: ["Audit", "Historical Intelligence", "Decision Memory"],
  },
  {
    id: "hestia",
    name: "Hestia",
    color: "#F59E0B",
    role: "Readiness Certification",
    capabilities: ["Readiness", "Qualification", "Operational Validation"],
  },
];

// Canonical identity color resolution for live registry rows.
//
// Surfaces that render agent_identities rows (e.g. the Agent Console roster)
// join a row to its fixed Pantheon identity the same way the network view
// model does: by case-insensitive name. Identity color comes ONLY from
// PANTHEON_AGENTS above — never from status, risk, tier, or card position.
// Unknown or future agents resolve to null so callers keep their existing
// neutral text treatment rather than inventing an identity.
export const PANTHEON_AGENT_COLOR_BY_NAME = Object.freeze(
  PANTHEON_AGENTS.reduce((byName, agent) => {
    byName[agent.name.toLowerCase()] = agent.color;
    return byName;
  }, {})
);

export function resolveAgentIdentityColor(name) {
  return PANTHEON_AGENT_COLOR_BY_NAME[String(name || "").trim().toLowerCase()] || null;
}

export const CROSS_LINKS = [
  { from: "zeus", to: "nike", kind: "governance", label: "Governance oversight" },
  { from: "zeus", to: "hephaestus", kind: "governance", label: "Governance oversight" },
  { from: "zeus", to: "hermes", kind: "governance", label: "Governance oversight" },
  { from: "zeus", to: "poseidon", kind: "governance", label: "Governance oversight" },
  { from: "poseidon", to: "hermes", kind: "delegation", label: "Delegates execution" },
  { from: "themis", to: "hermes", kind: "verification", label: "Verifies deployment" },
  { from: "hestia", to: "hermes", kind: "readiness", label: "Certifies readiness" },
  { from: "athena", to: "hephaestus", kind: "intent", label: "Strategy into build" },
  { from: "hephaestus", to: "hermes", kind: "build", label: "Build into deployment" },
];

const PUBLIC_FLOW = Object.freeze({ key: "decorative", label: "Decorative communication path" });

export const PUBLIC_PANTHEON_NETWORK = Object.freeze({
  core: Object.freeze({
    id: "pantheon",
    name: "Pantheon",
    label: "PANTHEON",
  }),
  nodes: Object.freeze(
    PANTHEON_AGENTS.map((agent) =>
      Object.freeze({
        id: agent.id,
        name: agent.name,
        role: agent.role,
        color: agent.color,
        capabilities: Object.freeze(agent.capabilities.slice()),
        flow: PUBLIC_FLOW,
        activityLevel: 0,
      })
    )
  ),
  crossLinks: Object.freeze(CROSS_LINKS.map((link) => Object.freeze({ ...link }))),
});
